// Coin gifts (notification center, Phase 4; mig 0287). A gift is an OFFER: the
// Coin moves only when the person accepts it (accept_coin_gift), within 7 days.
// Its state is DERIVED from the row's timestamps — the table has no status column,
// so there is nothing to disagree with. A withdrawn gift never reaches the app
// (RLS hides it), so it has no state here.

import type { CoinGift } from '../types';

export type GiftStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export function giftStatus(g: Pick<CoinGift, 'acceptedAt' | 'declinedAt' | 'expiresAt'>, now: number = Date.now()): GiftStatus {
  if (g.acceptedAt) return 'accepted';
  if (g.declinedAt) return 'declined';
  if (Date.parse(g.expiresAt) <= now) return 'expired';
  return 'pending';
}

/**
 * "6 days left", "5 hours left", "less than an hour left" — for a pending gift.
 * ⚠️ Rounds DOWN: claiming more time than is left is the dangerous direction (a
 * fresh 7-day gift reads "6 days left" — short of the truth, never over it).
 */
export function timeLeftLabel(expiresAt: string, now: number = Date.now()): string {
  const ms = Date.parse(expiresAt) - now;
  if (ms <= 0) return 'Expired';
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 48) return `${Math.floor(hours / 24)} days left`;
  if (hours >= 24) return '1 day left';
  if (hours >= 2) return `${hours} hours left`;
  if (hours === 1) return '1 hour left';
  return 'less than an hour left';
}

export const GIFT_STATUS_LABEL: Record<Exclude<GiftStatus, 'pending'>, string> = {
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

export interface CoinGiftRow {
  id: string;
  amount_cp: number;
  note: string | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  declined_at: string | null;
  withdrawn_at?: string | null;
}

export function giftFromRow(r: CoinGiftRow): CoinGift {
  return {
    id: r.id,
    amountCp: r.amount_cp,
    note: r.note ?? '',
    createdAt: r.created_at,
    expiresAt: r.expires_at,
    acceptedAt: r.accepted_at,
    declinedAt: r.declined_at,
  };
}
